import { TrendingUp, TrendingDown, Sparkles, Flame, Clock, PhoneMissed, Award, Activity } from 'lucide-react';
import clsx from 'clsx';
import type { TodayStats } from '@/lib/useTodayStats';
import type { WeeklyTrend } from '@/lib/useWeeklyTrend';

interface Insight {
  Icon: typeof Sparkles;
  text: React.ReactNode;
  /** Tailwind text-* sınıfı (vurgu rengi). */
  tone: 'brand' | 'emerald' | 'amber' | 'sky' | 'red';
}

/**
 * Bugünün öne çıkan metrikinden 1 cümlelik insight üretir.
 *
 * NOT: Süpervisör hassasiyeti — agent'a operasyonel direktif vermez
 * ("mola al", "yavaşla" gibi). Sadece nötr bilgi + performans odaklı
 * motivasyon mesajları. Self-care kararı agent'ın kendisinde kalır.
 * Mola tipi sağlık nasihati değil, basit bir durum raporudur.
 */
function generateInsight(stats: TodayStats, trend: WeeklyTrend): Insight {
  const sessionHrs = stats.sessionSec / 3600;

  // 1) Cevapsız çağrı uyarısı (kritik — agent farkında olsun)
  if (stats.callsMissed >= 3) {
    return {
      Icon: PhoneMissed,
      text: <><strong>{stats.callsMissed} cevapsız</strong> çağrı · bağlantı durumunu kontrol et</>,
      tone: 'red',
    };
  }

  // 2) Yoğun + dünden hızlı (kutlama)
  if (stats.callsAnswered >= 5 && trend.todayDeltaPct !== null && trend.todayDeltaPct >= 15) {
    return {
      Icon: TrendingUp,
      text: <>Bugün <strong>{stats.callsAnswered} çağrı</strong> · son 6 günden <strong>%{trend.todayDeltaPct} daha hızlı</strong></>,
      tone: 'emerald',
    };
  }

  // 3) Bugün sakin tempo (bilgi)
  if (stats.callsAnswered > 0 && trend.todayDeltaPct !== null && trend.todayDeltaPct <= -20) {
    return {
      Icon: TrendingDown,
      text: <>Bugünkü tempo son 6 günün ortalamasından <strong>%{Math.abs(trend.todayDeltaPct)} daha sakin</strong></>,
      tone: 'sky',
    };
  }

  // 4) Uzun bir çağrı yapıldı (overcome zorluğu vurgu)
  if (stats.maxCallSec >= 600) {
    const m = Math.floor(stats.maxCallSec / 60);
    return {
      Icon: Clock,
      text: <>Bugünün en uzun görüşmesi <strong>{m} dakika</strong> · iyi yönetilen bir çağrı</>,
      tone: 'brand',
    };
  }

  // 5) İlk çağrı henüz yok ama vardiya başladı
  if (stats.callsAnswered === 0 && stats.callsOutbound === 0 && stats.sessionSec > 600) {
    return {
      Icon: Sparkles,
      text: <>İlk çağrını bekliyoruz · <strong>kuyruğa girince</strong> akmaya başlar</>,
      tone: 'brand',
    };
  }

  // 6) Vardiya yarıyı geçti — sade bilgi (mola önerisi YOK)
  if (sessionHrs >= 4) {
    return {
      Icon: Activity,
      text: <><strong>{Math.round(sessionHrs)} saat</strong> aktiftesin · {stats.callsAnswered + stats.callsOutbound} çağrı tamamlandı</>,
      tone: 'brand',
    };
  }

  // 7) Streak (haftalık aktivite — supervisor-dostu motivasyon)
  const activeDays = trend.callsByDay.slice(0, 6).filter((v) => v > 0).length;
  if (activeDays >= 5) {
    return {
      Icon: Flame,
      text: <><strong>{activeDays}</strong> gündür bu hafta aktifsin · sürdürülebilir tempo</>,
      tone: 'amber',
    };
  }

  // 8) İlk çağrılar bitti — kazanım vurgu
  if (stats.callsAnswered >= 1 && stats.callsAnswered <= 3) {
    return {
      Icon: Award,
      text: <>Bugün <strong>{stats.callsAnswered} çağrı</strong> cevaplandı · iyi başlangıç</>,
      tone: 'emerald',
    };
  }

  // Default — generic durum raporu
  return {
    Icon: Sparkles,
    text: <>Bugün <strong>{stats.callsAnswered}</strong> cevap · <strong>{stats.callsOutbound}</strong> giden çağrı</>,
    tone: 'brand',
  };
}

const TONE_CLASSES = {
  brand:   { bg: 'bg-brand-50',    text: 'text-brand-700',    icon: 'text-brand-500',    ring: 'ring-brand-100' },
  emerald: { bg: 'bg-emerald-50',  text: 'text-emerald-700',  icon: 'text-emerald-500',  ring: 'ring-emerald-100' },
  amber:   { bg: 'bg-amber-50',    text: 'text-amber-700',    icon: 'text-amber-500',    ring: 'ring-amber-100' },
  sky:     { bg: 'bg-sky-50',      text: 'text-sky-700',      icon: 'text-sky-500',      ring: 'ring-sky-100' },
  red:     { bg: 'bg-red-50',      text: 'text-red-700',      icon: 'text-red-500',      ring: 'ring-red-100' },
};

export function AIInsight({ stats, trend }: { stats: TodayStats; trend: WeeklyTrend }) {
  const insight = generateInsight(stats, trend);
  const c = TONE_CLASSES[insight.tone];
  return (
    <div className={clsx('inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1', c.bg, c.ring)}>
      <insight.Icon size={14} className={c.icon} />
      <span className={clsx('text-[12.5px]', c.text)}>{insight.text}</span>
    </div>
  );
}
